<template>
  <DataTable
    :data="auditLogs"
    :columns="columns"
    :config="tableConfig"
  >
    <!-- Custom slot for timestamp column -->
    <template #timestamp="{ value }">
      <span class="text-sm text-gray-600 dark:text-gray-400">
        {{ formatTimestamp(value) }}
      </span>
    </template>

    <!-- Custom slot for method column -->
    <template #method="{ value }">
      <span
        :class="[
          'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
          getHttpMethodColor(value)
        ]"
      >
        {{ formatHttpMethod(value) }}
      </span>
    </template>

    <!-- Custom slot for resource column -->
    <template #resource="{ value }">
      <div class="flex flex-col">
        <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
          {{ value.type }}
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          ID: {{ value.id }}
        </span>
        <span
          v-if="value.name"
          class="text-xs text-gray-500 dark:text-gray-400 truncate"
          :title="value.name"
        >
          {{ value.name }}
        </span>
      </div>
    </template>

    <!-- Custom slot for change column -->
    <template #change="{ value }">
      <div v-if="value" class="flex flex-col space-y-1">
        <div class="text-xs text-gray-500 dark:text-gray-400">
          Field: <span class="font-medium">{{ value.field }}</span>
        </div>
        <div class="flex items-center space-x-2 text-xs">
          <span class="text-red-600 dark:text-red-400">
            {{ formatChangeValue(value.oldValue) }}
          </span>
          <span class="text-gray-400">→</span>
          <span class="text-green-600 dark:text-green-400">
            {{ formatChangeValue(value.newValue) }}
          </span>
        </div>
      </div>
      <div v-else class="text-xs text-gray-400 dark:text-gray-500 italic">
        No changes
      </div>
    </template>

    <!-- Custom slot for metadata column -->
    <template #metadata="{ value }">
      <div v-if="value" class="flex flex-col space-y-1">
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Service: <span class="font-medium">{{ value.service || 'N/A' }}</span>
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Env: <span class="font-medium">{{ value.environment || 'N/A' }}</span>
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          v{{ value.version || 'N/A' }}
        </span>
      </div>
      <div v-else class="text-xs text-gray-400 dark:text-gray-500 italic">
        No metadata
      </div>
    </template>

    <!-- Custom slot for user column -->
    <template #userId="{ value }">
      <span
        v-if="value"
        class="text-sm text-gray-900 dark:text-gray-100 font-mono"
      >
        {{ value }}
      </span>
      <span
        v-else
        class="text-sm text-gray-400 dark:text-gray-500 italic"
      >
        System
      </span>
    </template>

    <!-- Custom slot for URL column -->
    <template #url="{ value }">
      <span
        class="text-sm text-gray-600 dark:text-gray-400 font-mono truncate"
        :title="value"
      >
        {{ value }}
      </span>
    </template>
  </DataTable>
</template>

<script setup lang="ts">
import DataTable from '../../../components/shared/DataTable/index.vue'
import type { AuditLog } from '../../../types/audit-logs'
import { auditLogsService } from '../../../services/audit/auditLogsService'

interface Props {
  auditLogs: AuditLog[]
  loading?: boolean
}

const props = defineProps<Props>()

// Table configuration
const tableConfig = {
  enableSearch: true,
  enableSort: true,
  enableFilter: true,
  enablePagination: true,
  itemsPerPage: 20,
  searchPlaceholder: 'Search audit logs...',
  emptyMessage: 'No audit logs found',
  defaultSort: {
    field: 'timestamp',
    order: 'desc' as const
  }
}

// Table columns configuration
const columns = [
  {
    field: 'timestamp',
    label: 'Timestamp',
    sortable: true,
    type: 'date' as const,
    class: 'w-48'
  },
  {
    field: 'method',
    label: 'Method',
    sortable: true,
    class: 'w-20'
  },
  {
    field: 'resource',
    label: 'Resource',
    sortable: true,
    class: 'w-40'
  },
  {
    field: 'userId',
    label: 'User',
    sortable: true,
    class: 'w-32'
  },
  {
    field: 'change',
    label: 'Change',
    sortable: false,
    class: 'w-64'
  },
  {
    field: 'url',
    label: 'URL',
    sortable: false,
    class: 'w-64'
  },
  {
    field: 'metadata',
    label: 'Metadata',
    sortable: false,
    class: 'w-40'
  }
]

// Helper methods using the service
const formatTimestamp = auditLogsService.formatTimestamp
const formatHttpMethod = auditLogsService.formatHttpMethod
const formatChangeValue = auditLogsService.formatChangeValue
const getHttpMethodColor = auditLogsService.getHttpMethodColor
</script>
